import axios, { type CancelToken } from 'axios';
import { runSurveyFinalSubmission, type SurveySaveCoordinator } from './surveySaveCoordinator';

export const SURVEY_WRITE_TIMEOUT_MS = 30_000;

export class SurveyWriteTimeoutError extends Error {
  readonly code = 'SURVEY_WRITE_TIMEOUT';

  constructor() {
    super('Survey write timed out');
    this.name = 'SurveyWriteTimeoutError';
  }
}

type WriteRequest<T> = (token: CancelToken) => Promise<T>;
type RunWrite = <T>(request: WriteRequest<T>) => Promise<T>;

/** One deadline covers queueing, authentication, draft flush and completion.
 * Cancellation is attempt-local. Racing each step also releases the save queue
 * if token acquisition or a transport never settles after cancellation.
 */
const withWriteDeadline = async <T>(operation: (run: RunWrite) => Promise<T>): Promise<T> => {
  const source = axios.CancelToken.source();
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      source.cancel('Survey write deadline exceeded');
      reject(new SurveyWriteTimeoutError());
    }, SURVEY_WRITE_TIMEOUT_MS);
  });
  const run: RunWrite = (request) => Promise.race([
    Promise.resolve().then(() => {
      // A timed-out queued attempt must never send later or start completion.
      source.token.throwIfRequested();
      return request(source.token);
    }),
    deadline,
  ]);

  try {
    return await Promise.race([operation(run), deadline]);
  } finally {
    clearTimeout(timer!);
  }
};

export const writeSurvey = <T>(request: WriteRequest<T>): Promise<T> =>
  withWriteDeadline((run) => run(request));

export const submitSurveyWithDeadline = <T>(
  coordinator: SurveySaveCoordinator,
  persistDraft: WriteRequest<unknown>,
  complete: WriteRequest<T>,
): Promise<T> => withWriteDeadline((run) => runSurveyFinalSubmission(
  coordinator,
  () => run(persistDraft),
  () => run(complete),
));
